import {collection, getFirestore, doc, getDocs, updateDoc, query, where, getDoc, DocumentReference, deleteDoc, addDoc } from 'firebase/firestore';
import Review from '../interfaces/Review';
import app from '../firebaseConfig'
import Location from '../interfaces/Location';
import Place from '../interfaces/Place';
import UserLocationReview from '../interfaces/UserLocationReview';
import LocationReview from '../interfaces/LocationReview';
import LocationReviewIds from '../interfaces/LocationReviewIds';


const dbActions = {
  db: getFirestore(app),

  async getReviews(locationId: string): Promise<Review[]> {
    const locationDocRef = doc(this.db, 'locations', locationId);
    const reviewsSnapshot = await getDocs(collection(locationDocRef, "reviews"));
    const retrievedReviews: Review[] = reviewsSnapshot.docs.map((doc) => {
      const data = doc.data() as Review; // Assuming doc.data() already conforms to Review
      return {
          id: doc.id,
          ...data // Spread the rest of the data
      };
    })
    return retrievedReviews;
  },

  async getReview(locationId: string, reviewId: string): Promise<{ docRef: DocumentReference; review: Review; }> {
    const locationDocRef = doc(this.db, 'locations', locationId);
    const reviewsDocRef = doc(collection(locationDocRef, "reviews"), reviewId);
    const retrievedReview: Review = (await getDoc(reviewsDocRef)).data() as Review;
    return {docRef: reviewsDocRef, review: retrievedReview};
  },

  async addReview(locationDocRef: DocumentReference, review: Review): Promise<DocumentReference> {
    const reviewRef = await addDoc((collection(locationDocRef, 'reviews')), review);
    return reviewRef;
  },

  async getLocations(): Promise<Location[]> {
    const locationCollectionRef = collection(this.db, 'locations');
    const locationsSnapshot = await getDocs(locationCollectionRef)
    const retrievedLocations: Location[] = locationsSnapshot.docs.map((doc) => {
      return { locationId: doc.id, place: doc.data() as Place } as Location;
    })
    return retrievedLocations;
  },

  async getLocation(locationId: string): Promise<{ docRef: DocumentReference; location: Place; }> {
    const locationDocRef = doc(this.db, 'locations', locationId);
    const retrievedLocation: Place = (await getDoc(locationDocRef)).data() as Place;
    return {docRef: locationDocRef, location: retrievedLocation};
  },

  async addLocationIfNotExists(place: Place): Promise<DocumentReference> {
    const locationsRef = collection(this.db, 'locations');
    const locationsDocs = await getDocs(query(locationsRef, where('address', '==', place.address)));
    if (!locationsDocs.empty) {
      // If a matching document exists, return its ID
      const existingDoc = locationsDocs.docs[0];
      return existingDoc.ref;
    } else {
      // If no matching document is found, add a new document
      const locationRef = await addDoc(locationsRef, place);
      return locationRef;
    }
  },

  async getUserFromEmail(email: string): Promise<{ docRef: DocumentReference; user: UserLocationReview; }> {
    const usersRef = collection(this.db, 'users');
    const snapshot = await getDocs(query(usersRef, where('email', '==', email)));
    
    try {
      const userDoc = snapshot.docs[0];
      const user: UserLocationReview = userDoc.data() as UserLocationReview;
      return { docRef: userDoc.ref, user: user};
    } catch { // user doesn't exist
      const docRef = await this.addUserEmail(email);
      return { docRef: docRef, user: {email: email, locationIds: []} as UserLocationReview};
    }
  },

  async getUserReviews(userData: UserLocationReview) : Promise<LocationReview[]> {
    const retrievedLocationReviewIds: LocationReviewIds[] = userData.locationIds;
    const retrievedReviewsPromises = retrievedLocationReviewIds.map(async (locationReviewId) => {
      const locationData = await this.getLocation(locationReviewId.locationId);
      const unfilteredReviews = await this.getReviews(locationReviewId.locationId); 
      const reviews = unfilteredReviews.filter((review) => locationReviewId.reviewIds.includes(review.id!))
      const locationReviewPromises = reviews.map(async (review) => {
        return {
          locationId: locationReviewId.locationId,
          address: locationData.location.address,
          review: review
        }
      });

      const locationReviews = await Promise.all(locationReviewPromises);
      return locationReviews;
    });

    const retrievedReviewsMatrix = await Promise.all(retrievedReviewsPromises);
    const retrievedReviews: LocationReview[] = retrievedReviewsMatrix.flat();
    return retrievedReviews;
  },

  async addUser(email: string, locationId: string, reviewId: string): Promise<DocumentReference> {
    const usersRef = collection(this.db, "users") //quedo algo raro aca
    return await addDoc(usersRef, {
      email: email,
      locationIds: [{ locationId: locationId, reviewIds: [reviewId] }]
    });
  },

  async addUserEmail(email: string): Promise<DocumentReference> {
    const usersRef = collection(this.db, "users") //quedo algo raro aca
    return await addDoc(usersRef, {
      email: email,
      locationIds: []
    });
  },

  async deleteDocument(docRef: DocumentReference) {
    await deleteDoc(docRef);
  },

  async updateDocument(docRef: DocumentReference, data: any) {
    const updatedData: { [x: string]: any; } = data;
    await updateDoc(docRef, updatedData);
  }
}

export default dbActions;
